import { useEffect, useRef, useCallback, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { Tabs } from "@heroui/react";
import Icon from "@mdi/react";
import { mdiAlertCircleOutline } from "@mdi/js";
import { contractorDetailStore } from "@/stores/ContractorDetailStore";
import { ROUTES } from "@/constants/routes";
import ContractorHeader from "./components/ContractorHeader";
import ContractorMetrics from "./components/ContractorMetrics";
import NoLegalDataCard from "./LegalDataDrawer/components/views/NoLegalDataCard";
import TransactionsTab from "./components/TransactionsTab";
import BookingsTab from "./components/BookingsTab";
import PayoutsTab from "./components/PayoutsTab";
import LegalInfoTab from "./LegalDataDrawer/components/views/LegalInfoTab";
import LegalDataDrawer from "./LegalDataDrawer";
import LegalSuccessModal from "./LegalDataDrawer/components/views/LegalSuccessModal";

import PayoutModal from "./components/PayoutModal";
import { ServiceCenterDrawer } from "@/features/constractors";
import "./ContractorDetailPage.css";
import TransactionDetailModal from "@/shared/ui/DetailModal/TransactionDetailModal";

const TABS = [
  { id: "transactions", label: "История операций" },
  { id: "bookings", label: "Бронирования" },
  { id: "payouts", label: "Выплаты" },
  { id: "legal", label: "Юридическая информация" },
] as const;

type TabId = (typeof TABS)[number]["id"];

const ContractorDetailPage: React.FC = observer(() => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const store = contractorDetailStore;
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>("transactions");

  useEffect(() => {
    scrollContainerRef.current = document.querySelector(".app-layout__content");
  }, []);

  const handleTabChange = useCallback((tabId: TabId) => {
    const el = scrollContainerRef.current;
    const saved = el?.scrollTop ?? 0;
    setActiveTab(tabId);
    if (el) {
      requestAnimationFrame(() => {
        el.scrollTop = saved;
      });
    }
  }, []);

  useEffect(() => {
    if (uuid) {
      store.loadContractor(uuid);
      store.loadStats(uuid);
    }
    return () => {
      store.reset();
    };
  }, [uuid]);

  if (store.isLoading) {
    return (
      <div className="scd-page" data-testid="contractor-detail-page">
        <div className="scd-page__loading">Загрузка...</div>
      </div>
    );
  }

  if (!store.contractor) {
    return (
      <div className="scd-page" data-testid="contractor-detail-page">
        <div className="scd-page__not-found">
          <p>Контрагент не найден</p>
          <button
            className="scd-back"
            onClick={() => navigate(ROUTES.CONTRACTORS)}
            type="button"
          >
            Вернуться к списку
          </button>
        </div>
      </div>
    );
  }

  const isLegalFilled = store.legal.status.isLegalDataFilled;
  const isLegalChecking = store.legal.status.isLoadingCheck;

  const isTabDisabled = (tabId: TabId): boolean =>
    !isLegalFilled && (tabId === "payouts" || tabId === "legal");

  return (
    <div className="scd-page" data-testid="contractor-detail-page">
      <ContractorHeader />

      {/* Метрики: скелетон → графики → заглушка */}
      {isLegalChecking ? (
        <div className="scd-legal-gate">
          <div className="scd-legal-gate__skeleton" />
          <div className="scd-legal-gate__skeleton scd-legal-gate__skeleton--sm" />
          <div className="scd-legal-gate__skeleton scd-legal-gate__skeleton--sm" />
        </div>
      ) : isLegalFilled ? (
        <ContractorMetrics />
      ) : (
        <NoLegalDataCard />
      )}

      <div className="scd-tabs-wrap">
        <Tabs
          className="scd-tabs"
          selectedKey={activeTab}
          onSelectionChange={(key) => {
            const k = key as TabId;
            if (isTabDisabled(k)) return;
            handleTabChange(k);
          }}
        >
          <Tabs.ListContainer>
            <Tabs.List aria-label="Разделы контрагента">
              {TABS.map((tab) => {
                const disabled = isTabDisabled(tab.id);
                return (
                  <Tabs.Tab key={tab.id} id={tab.id} isDisabled={disabled}>
                    {disabled ? (
                      <span
                        className="scd-tab-label scd-tab-tooltip-wrap"
                        data-tooltip="Заполните юридические данные, чтобы открыть этот раздел"
                      >
                        {tab.label}
                        <span className="scd-tab-error-badge">
                          <Icon path={mdiAlertCircleOutline} size={0.55} />
                        </span>
                      </span>
                    ) : (
                      <span className="scd-tab-label">{tab.label}</span>
                    )}
                    <Tabs.Indicator />
                  </Tabs.Tab>
                );
              })}
            </Tabs.List>
          </Tabs.ListContainer>

          <Tabs.Panel id="transactions">
            <TransactionsTab />
          </Tabs.Panel>
          <Tabs.Panel id="bookings">
            <BookingsTab />
          </Tabs.Panel>
          {isLegalFilled && (
            <Tabs.Panel id="payouts">
              <PayoutsTab />
            </Tabs.Panel>
          )}
          {isLegalFilled && (
            <Tabs.Panel id="legal">
              <LegalInfoTab />
            </Tabs.Panel>
          )}
        </Tabs>
      </div>

      <TransactionDetailModal pd={store.pd} />
      <PayoutModal />
      <LegalDataDrawer />
      <LegalSuccessModal />
      <ServiceCenterDrawer
        onSuccess={() => uuid && store.loadContractor(uuid)}
      />
    </div>
  );
});

ContractorDetailPage.displayName = "ContractorDetailPage";

export default ContractorDetailPage;
